import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ALL_DESTINATIONS } from '../nav'
import { VIEWS } from '../navViews'

/**
 * One destination, at its own URL (D-322, D-326).
 *
 * **Now the width it always owed.** This started as a holding shape: step 2 of
 * the navigation spec made the portal tabs addressable and reproduced exactly
 * what the portal had given these components — `portal-shell`, 60rem at a
 * laptop — so that any visual regression belonged to one commit or the other
 * and not to both. Step 3 shipped the rail, which was the stated exit
 * condition, so the width question is now answered: `page-shell`, like every
 * other signed-in working screen (D-225).
 *
 * **`page-shell` carries no padding of its own**, so it is added here rather
 * than in the utility, which is also used inside already-padded containers.
 *
 * **News keeps a narrower measure, deliberately.** Facebook caps its embed at
 * 500px, so a wide column leaves it centred in mostly nothing — the same
 * exception prose and sign-in cards have always had. An explicit prop rather
 * than a special case inside here, because the next screen that wants it will
 * not be news.
 *
 * **The heading names the view when there is one, and the destination when
 * there is not** — never a second copy of either. Two strings for one name
 * drift, and the drift renders as a rail item and a page title disagreeing
 * about where you are.
 *
 * **The view half was found by looking at the screen.** The destination label
 * was right while every destination was one screen; once Cover became the
 * second view of Classes (D-332), both views were headed "Classes" and only a
 * small chip said which one you were on — so a list of teacher absences sat
 * under a heading naming the class list. The title has to agree with where you
 * actually are, which is the same rule one level deeper.
 */
export default function DestinationScreen({
  destination,
  measure = 'page',
  children,
}: {
  destination: string
  measure?: 'page' | 'narrow'
  children: ReactNode
}) {
  const { pathname } = useLocation()

  /*
   * Longest match wins, exactly as `destinationForPath` does it — a shallower
   * view must not answer for a deeper one sitting underneath it.
   *
   * **Nothing exercises the sort today and it is kept deliberately.** No
   * destination in `VIEWS` has two entries that can match one path, because the
   * `end` flags prevent it; a mutation removing the sort leaves every test
   * green. It stays because the sibling function needs it for real and two
   * implementations of one lookup drifting is worse than a line that is
   * currently redundant — but do not read a test as covering it.
   */
  const view = (VIEWS[destination] ?? [])
    .filter((v) => (v.end ? pathname === v.to : pathname.startsWith(v.to)))
    .sort((a, b) => b.to.length - a.to.length)[0]

  // A tab is narrow and a heading is not, so a view may say more here (D-464).
  const label =
    view?.heading ?? view?.label ?? ALL_DESTINATIONS.find((d) => d.key === destination)?.label
  // `py-8` because the other 23 `page-shell` screens use it. A 24th variant is
  // the accumulation D-225 existed to collapse, not a fresh choice.
  const shell = measure === 'narrow' ? 'portal-shell' : 'page-shell px-5 py-8'

  return (
    <div className={`${shell} flex min-h-full flex-col`}>
      {/*
        `text-2xl`, which is what a page heading is on a signed-in working
        screen: of the 19 headings in components using `page-shell`, 14 already
        said `font-display text-2xl`, and this was one that did not (D-335).
      */}
      {label && <h1 className="mb-4 font-display text-2xl">{label}</h1>}
      {children}
    </div>
  )
}
